import * as React from 'react';

import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import SubmitIcon from '@mui/icons-material/Publish';
import ApproveIcon from '@mui/icons-material/DoneAll';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';

import { yupResolver } from '@hookform/resolvers/yup';
import ControlledForm from '../../components/ControlledForm';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ScrollToTop from '../../components/ScrollToTop';

import FormReadOnlySection from '../../components/FormReadOnlySection';
import FormPaper from '../../components/FormPaper';
import FormTitle from '../../components/FormTitle';
import FormHeader from '../../components/FormHeader';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useFlightEventReportData } from '../../core/context/FlightEventReportDataContext';
import useDeleteFightEventDraft from '../../core/api/hooks/flight-event/useDeleteFightEventDraft';
import { EventError, EventFormStatus, FlightEventReport } from '../../core/api/clients';

import FlightStep from './FormSteps/FlightStep';
import EventStep from './FormSteps/EventStep';
import WeatherStep from './FormSteps/WeatherStep';
import NarrativeStep from './FormSteps/NarrativeStep';

import '../../core/styles/disabledFields.css';
import FormStatus from '../../components/FormStatus';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import FlightEventFormValidationSchema from './FlightEventFormValidationSchema';
import queryKeys from '../../core/api/queryKeys';
import ApprovalDialog from '../../components/ApprovalDialog';
import { getColor } from '../../core/colors';

function FlightEventFormView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authorizedFormData = useAuthorizedFormData();
  const { enqueueSnackbar } = useSnackbar();

  const {
    flightEventReport,
    update,
    approve,
    submit,
    allowedActions,
    submitStatus,
    approveStatus,
    sendNotification,
    sendNotificationStatus,
  } = useFlightEventReportData();

  const { mutate: deleteFlightEventDraft } = useDeleteFightEventDraft();
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);
  const [approverDialogOpen, setApproverDialogOpen] = React.useState<boolean>(false);
  const [triggerWorkflow, setTriggerWorkflow] = React.useState<boolean>(false);

  const formMethods = useForm<FlightEventReport>({
    defaultValues: flightEventReport,
    mode: 'onBlur',
    resolver: yupResolver(FlightEventFormValidationSchema),
  });

  const submitCallback = () => {
    Promise.all([
      queryClient.refetchQueries({ queryKey: [queryKeys.FlightEvent.Drafts] }),
      queryClient.refetchQueries({ queryKey: [queryKeys.FlightEvent.Submissions] }),
    ]).then(() => {
      navigate('/');
    });
  };

  const handleUpdate = () => {
    update(formMethods.getValues());
  };

  const handleSubmit = () => {
    submit(flightEventReport.id, submitCallback);
  };
  const approveCallback = (eventReport: FlightEventReport) => {
    // setting timeout for navigating allows refetch to finish
    // and display updated data in the approval list
    queryClient
      .refetchQueries({ queryKey: [queryKeys.FlightEvent.ApprovalList] })
      .then(() => {
        navigate('/form/flight-event', {
          state: {
            isError: false,
            title: 'The report was successfully approved',
            message: 'The report was successfully approved',
          },
        });
      });
  };

  const approveFailCallback = (error: EventError) => enqueueSnackbar(error.message, { variant: 'error' });

  const handleApprove = (approvalComments: string | null | undefined, notificationType: string | null | undefined) => {
    approve(
      {
        eventReportId: flightEventReport.id,
        triggerWorkflow,
        approvalComments,
        notificationType,
      },
      approveCallback,
      approveFailCallback,
    );
  };
  const handleSendNotification = (approvalComments: string | null | undefined, notificationType: string | null | undefined) => {
    sendNotification(
      {
        eventReportId: flightEventReport.id,
        triggerWorkflow: true,
        approvalComments,
        notificationType,
      },
      () => queryClient.refetchQueries({ queryKey: [queryKeys.FlightEvent.ApprovalList] }),
    );
  };
  const handleDelete = () => {
    if (flightEventReport.id) {
      deleteFlightEventDraft(flightEventReport.id, {
        onSuccess: () => {
          queryClient
            .refetchQueries({ queryKey: [queryKeys.FlightEvent.ApprovalList] })
            .then(() => {
              navigate('/form/flight-event');
            });
        },
        onError: () => enqueueSnackbar('Error deleting form', { variant: 'error' }),
      });
    }
  };

  const openDeleteConfirmationDialog = () => {
    setConfDialogOpen(true);
  };
  React.useEffect(() => {
    formMethods.reset(flightEventReport);
  }, [flightEventReport]);
  const handleWorkflowComments = (comments: string | null | undefined, type: string | null | undefined) => {
    if (flightEventReport.status === EventFormStatus.Final || flightEventReport.status === EventFormStatus.Approved) {
      handleSendNotification(comments ?? flightEventReport.narrative, type);
    } else {
      handleApprove(comments ?? flightEventReport.narrative, type);
    }
  };

  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <ScrollToTop />
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleDelete} />
      <ApprovalDialog
        open={approverDialogOpen}
        onClose={() => setApproverDialogOpen(false)}
        textOptions={
          [flightEventReport.narrative, flightEventReport.approverComments]
            .filter((value) => value !== null && value !== '')
        }
        typeOptions={['General', '121.557']}
        onSelection={handleWorkflowComments}
        primaryColor={getColor(authorizedFormData.metadata?.color)[200]}
        secondaryColor={getColor(authorizedFormData.metadata?.color)[300]}
      />
      <FormHeader>
        <FormTitle
          title="Flight Event Report"
          subtitle={[[
            'Reported By:',
            flightEventReport.employeeName,
            `[${flightEventReport.employeeId}],`,
            flightEventReport.employeeLocation,
            flightEventReport.employeeTitle,
          ].join(' ')]}
        />
        <FormStatus
          status={flightEventReport.status}
          subtext={undefined}
        />
      </FormHeader>
      <ControlledForm onUpdate={handleUpdate} enableUpdate>
        <FormProvider {...formMethods}>
          <FormValidationProvider<FlightEventReport>>
            <Stack spacing={2}>
              <FormReadOnlySection title="Flight & Crew">
                <FlightStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Event">
                <EventStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Weather">
                <WeatherStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Narrative">
                <NarrativeStep visible readOnly />
              </FormReadOnlySection>
            </Stack>
          </FormValidationProvider>
        </FormProvider>
      </ControlledForm>
      <Box display="flex" flexDirection="row" sx={{ my: 2, justifyContent: 'flex-end' }}>
        <Collapse in={allowedActions?.approve}>
          <FormControlLabel
            sx={{ mr: 2 }}
            control={(
              <RadioGroup
                row
                value={triggerWorkflow}
              >
                <FormControlLabel
                  value
                  control={<Radio onClick={() => setTriggerWorkflow(true)} />}
                  label="Yes"
                  labelPlacement="start"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio onClick={() => setTriggerWorkflow(false)} />}
                  label="No"
                  labelPlacement="start"
                />
              </RadioGroup>
        )}
            label="Notify the FAA: "
            labelPlacement="start"
          />
        </Collapse>
      </Box>
      <Box display="flex" sx={{ my: 2 }}>
        <Button
          sx={{ marginRight: 2 }}
          onClick={() => navigate('/form/flight-event')}
        >
          Close
        </Button>
        {allowedActions?.delete && (
          <Button
            sx={{ marginRight: 2 }}
            color="error"
            variant="outlined"
            onClick={openDeleteConfirmationDialog}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        {allowedActions?.submit && (
          <LoadingButton
            loading={submitStatus === 'pending'}
            loadingPosition="end"
            endIcon={<SubmitIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Submit (Unsaved)' : 'Submit'}`}
          </LoadingButton>
        )}
        {allowedActions?.edit && (
          <Button
            sx={{ marginRight: 2 }}
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/form/flight-event/${flightEventReport.id}/edit`)}
          >
            Edit
          </Button>
        )}
        {allowedActions?.approve && (
          <LoadingButton
            loading={approveStatus === 'pending'}
            loadingPosition="end"
            endIcon={<ApproveIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={() => {
              if (triggerWorkflow) {
                setApproverDialogOpen(true);
              } else {
                handleApprove(flightEventReport.narrative, null);
              }
            }}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Approve (Unsaved)' : 'Approve'}`}
          </LoadingButton>
        )}
        {authorizedFormData.isApprover && (flightEventReport.status === EventFormStatus.Approved || flightEventReport.status === EventFormStatus.Final)
        && (
          <LoadingButton
            loading={sendNotificationStatus === 'pending'}
            loadingPosition="end"
            endIcon={<ApproveIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={() => setApproverDialogOpen(true)}
            disabled={formMethods.formState.isDirty}
          >
            {`${flightEventReport.faaNotificationType !== null ? 'Re-send Faa Notification' : 'Send Faa Notification'}`}
          </LoadingButton>
        )}
      </Box>
    </FormPaper>
  );
}

export default FlightEventFormView;
